import type { ListBuilderColumn } from '../../types';
import { MERCHANDISER_STATUS_VALUE_MAPPING } from './shared';

/** Merchandiser name (link to detail) with manufacturer/model subtext. */
export const createMerchandiserNameColumn = (): ListBuilderColumn => ({
	id: 'merchandiser-name',
	headers: [{ id: 'merchandiser-name-header', text: 'Merchandiser' }],
	fields: [
		{
			id: 'name-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'name', objectTable: 'merchandiser' },
			},
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'text',
		},
		{
			id: 'line-break-1',
			dataConfig: { type: 'custom_text', customText: '' },
			displayStyle: 'line_break',
		},
		{
			// PD-14339 — managed-catalog make/model shown together as subtext
			// (e.g. "True TDD-2"). Was the dropped free-text `manufacturer` field.
			id: 'manufacturer-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'manufacturerName', objectTable: 'merchandiser' },
			},
			styleConfig: { size: 'sm', colorRule: { type: 'muted' } },
			displayStyle: 'text',
		},
		{
			id: 'make-model-space',
			dataConfig: { type: 'custom_text', customText: ' ' },
			styleConfig: { size: 'sm', colorRule: { type: 'muted' } },
			displayStyle: 'text',
		},
		{
			id: 'model-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'modelName', objectTable: 'merchandiser' },
			},
			styleConfig: { size: 'sm', colorRule: { type: 'muted' } },
			displayStyle: 'text',
		},
	],
	sortable: true,
});

/** Placement — the customer / address the unit sits at (or warehouse). */
export const createPlacementColumn = (): ListBuilderColumn => ({
	id: 'placement',
	headers: [{ id: 'placement-header', text: 'Placement' }],
	fields: [
		{
			id: 'placement-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'locationInfo', objectTable: 'merchandiser' },
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'text',
		},
	],
	sortable: false,
	minWidth: 220,
});

/** Status badge. */
export const createStatusColumn = (): ListBuilderColumn => ({
	id: 'status',
	headers: [{ id: 'status-header', text: 'Status' }],
	fields: [
		{
			id: 'status-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'status', objectTable: 'merchandiser' },
			},
			styleConfig: {
				size: 'sm',
				valueMapping: [...MERCHANDISER_STATUS_VALUE_MAPPING],
			},
			displayStyle: 'badge',
		},
	],
	sortable: true,
	width: 130,
});

/** Number of locations (child locations) the merchandiser owns. */
export const createBinsColumn = (): ListBuilderColumn => ({
	id: 'bins',
	headers: [{ id: 'bins-header', text: 'Locations' }],
	fields: [
		{
			id: 'bins-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'locationsCount', objectTable: 'merchandiser' },
				defaultValue: '0',
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'text',
		},
	],
	sortable: false,
	width: 90,
});

/** Fill % rolled up across the merchandiser's locations. */
export const createFillColumn = (): ListBuilderColumn => ({
	id: 'fill',
	headers: [{ id: 'fill-header', text: 'Fill %' }],
	fields: [
		{
			id: 'fill-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'fillPercentage', objectTable: 'merchandiser' },
				defaultValue: '0',
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'text',
		},
	],
	sortable: false,
	width: 100,
});

/** Stock level (Empty / Low / Medium / Full). */
export const createStockLevelColumn = (): ListBuilderColumn => ({
	id: 'stock-level',
	headers: [{ id: 'stock-level-header', text: 'Stock' }],
	fields: [
		{
			id: 'stock-level-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'stockLevel', objectTable: 'merchandiser' },
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'text',
		},
	],
	sortable: false,
	width: 110,
});

export const createMerchandiserColumns = (): Array<ListBuilderColumn> => [
	createMerchandiserNameColumn(),
	createPlacementColumn(),
	createStatusColumn(),
	createBinsColumn(),
	createFillColumn(),
	createStockLevelColumn(),
];
